import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import DataTable from "../../../Pages/InputField/DataTable"; // Import the reusable DataTable component
import baseURL from "../../../../Url/NodeBaseURL";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";

const DetailedSalesReport = () => {
  const [data, setData] = useState([]); // State to store table data
  const [loading, setLoading] = useState(true); // Loading state
  const [filteredRows, setFilteredRows] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${baseURL}/get/repair-details`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const dataArray = Array.isArray(result) ? result : [result];

        // ✅ Show only Sales transactions
        const salesData = dataArray.filter(
          (item) => item.transaction_status === "Sales",
        );

        // 🔽 Sort newest first
        const sortedData = salesData.sort(
          (a, b) => new Date(b.date) - new Date(a.date),
        );

        setData(sortedData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const downloadExcel = () => {
    const exportData = filteredRows.length ? filteredRows : data;

    const excelData = exportData.map((item, index) => {
      const amount =
        (Number(item.rate_amt) || 0) +
        (Number(item.stone_price) || 0) +
        (Number(item.making_charges) || 0) +
        (Number(item.hm_charges) || 0);

      const discount = Number(item.disscount) || 0;
      const taxableAmount = amount - discount;
      const gst = Number(item.tax_amt) || 0;
      const totalAmount = taxableAmount + gst;

      return {
        "S No": index + 1,
        Date: item.date ? new Date(item.date).toLocaleDateString("en-GB") : "",
        Customer: item.account_name || "",
        "Bill No": item.invoice_number || "",
        "Article Purchased":
          `${item.metal_type || ""} ${item.product_name || ""}`.trim(),
        "Gross Wt": item.gross_weight || "",
        "Final Wt": item.total_weight_av || "",
        Amount: amount.toFixed(2),
        Discount: discount.toFixed(2),
        "Taxable Amt": taxableAmount.toFixed(2),
        "GST Value": gst.toFixed(2),
        "Total Amt": totalAmount.toFixed(2),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Report");

    XLSX.writeFile(
      workbook,
      `Sales_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  // Define columns for the table
  const columns = React.useMemo(
    () => [
      {
        Header: "S No",
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString("en-GB") : "",
      },
      {
        Header: "Customer",
        accessor: "account_name",
      },
      {
        Header: "Bill No",
        accessor: "invoice_number",
      },
      {
        Header: "Article Purchased",
        id: "article_purchased",
        accessor: (row) =>
          `${row.metal_type || ""} ${row.product_name || ""}`.trim(),
      },
      {
        Header: "Gross Wt",
        accessor: "gross_weight",
      },
      {
        Header: "Final Wt",
        accessor: "total_weight_av",
      },

      /* ---------------- AMOUNT ---------------- */
      {
        Header: "Amount",
        accessor: (row) => {
          const amount =
            (Number(row.rate_amt) || 0) +
            (Number(row.stone_price) || 0) +
            (Number(row.making_charges) || 0) +
            (Number(row.hm_charges) || 0);

          return amount;
        },
        Cell: ({ value }) => value.toFixed(2),
      },

      /* ---------------- DISCOUNT ---------------- */
      {
        Header: "Discount",
        accessor: (row) => Number(row.disscount) || 0,
        Cell: ({ value }) => value.toFixed(2),
      },

      /* ---------------- TAXABLE AMOUNT ---------------- */
      {
        Header: "Taxable Amt",
        accessor: (row) => {
          const amount =
            (Number(row.rate_amt) || 0) +
            (Number(row.stone_price) || 0) +
            (Number(row.making_charges) || 0) +
            (Number(row.hm_charges) || 0);

          const discount = Number(row.disscount) || 0;

          return amount - discount;
        },
        Cell: ({ value }) => value.toFixed(2),
      },

      /* ---------------- GST VALUE ---------------- */
      {
        Header: "GST Value",
        accessor: (row) => Number(row.tax_amt) || 0,
        Cell: ({ value }) => value.toFixed(2),
      },

      /* ---------------- TOTAL AMOUNT ---------------- */
      {
        Header: "Total Amt",
        accessor: (row) => {
          const amount =
            (Number(row.rate_amt) || 0) +
            (Number(row.stone_price) || 0) +
            (Number(row.making_charges) || 0) +
            (Number(row.hm_charges) || 0);

          const discount = Number(row.disscount) || 0;
          const taxableAmt = amount - discount;
          const gst = Number(row.tax_amt) || 0;

          return taxableAmt + gst;
        },
        Cell: ({ value }) => value.toFixed(2),
      },
    ],
    [],
  );

  return (
    <div className="main-container">
      <div className="sales-table-container">
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <h3>Detailed Sales Report</h3>
            <Button
              variant="success"
              onClick={downloadExcel}
              disabled={loading || data.length === 0}
              className="d-flex align-items-center gap-2"
            >
              <FaFileExcel />
              Download Excel
            </Button>
          </Col>
        </Row>
        {loading ? (
          <p>Loading...</p> // Show loading message while fetching data
        ) : (
          <DataTable
            columns={columns}
            data={data}
            onFilterChange={setFilteredRows}
          />
        )}
      </div>
    </div>
  );
};

export default DetailedSalesReport;
